// src/lib/geocode.js
// Free geocoding using Nominatim (OpenStreetMap)

const NOMINATIM_BASE = 'https://nominatim.openstreetmap.org/search';

export async function geocodeLocation(locationName, city = '') {
  if (!locationName) return null;
  
  const query = city ? `${locationName}, ${city}` : locationName;
  const params = new URLSearchParams({
    q: query,
    format: 'json',
    limit: '1',
    addressdetails: '0',
  });

  try {
    const response = await fetch(`${NOMINATIM_BASE}?${params}`, {
      headers: {
        'User-Agent': 'GreatBreak/1.0',
      },
    });
    
    const data = await response.json();
    
    if (data && data.length > 0) {
      return {
        lat: parseFloat(data[0].lat),
        lng: parseFloat(data[0].lon),
        name: locationName,
      };
    }
    
    return null;
  } catch (error) {
    console.error('Geocoding error:', error);
    return null;
  }
}

export async function geocodeLocations(locationNames, city = '') {
  const results = await Promise.all(
    locationNames.map(async (name) => {
      const result = await geocodeLocation(name, city);
      return result;
    })
  );
  
  return results.filter(Boolean);
}

// Cache for geocoded results
const geocodeCache = new Map();

export async function geocodeWithCache(locationName, city = '') {
  const key = `${locationName}-${city}`;
  
  if (geocodeCache.has(key)) {
    return geocodeCache.get(key);
  }
  
  // Rate limit: wait 1 second between requests (Nominatim policy)
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  const result = await geocodeLocation(locationName, city);
  
  if (result) {
    geocodeCache.set(key, result);
  }
  
  return result;
}

// Client-side gazetteer of common travel destinations, keyed by a lowercase
// substring of the destination name. Destination records don't carry lat/lng,
// so this resolves coordinates synchronously (no network) to plot them on the
// map. Unresolved names return null and are omitted from the map. Extend this
// list as new seed destinations are added, or fall back to geocodeLocation().
export const GAZETTEER = [
  ['bali', [-8.4095, 115.1889]],
  ['tokyo', [35.6762, 139.6503]],
  ['santorini', [36.3932, 25.4615]],
  ['machu picchu', [-13.1631, -72.545]],
  ['paris', [48.8566, 2.3522]],
  ['safari', [-1.2921, 36.8219]],
  ['kenya', [-1.2921, 36.8219]],
  ['maldives', [3.2028, 73.2207]],
  ['swiss alps', [46.8182, 8.2275]],
  ['switzerland', [46.8182, 8.2275]],
  ['bangkok', [13.7563, 100.5018]],
  ['thailand', [13.7563, 100.5018]],
  ['ibiza', [38.9067, 1.4206]],
  ['spain', [40.4168, -3.7038]],
  ['rishikesh', [30.0869, 78.2676]],
  ['india', [28.6139, 77.209]],
  ['dubai', [25.2048, 55.2708]],
  ['uae', [25.2048, 55.2708]],
  ['rome', [41.9028, 12.4964]],
  ['italy', [41.9028, 12.4964]],
  ['new york', [40.7128, -74.006]],
  ['london', [51.5074, -0.1278]],
  ['barcelona', [41.3874, 2.1686]],
  ['lisbon', [38.7223, -9.1393]],
  ['sydney', [-33.8688, 151.2093]],
  ['cape town', [-33.9249, 18.4241]],
  ['iceland', [64.1466, -21.9426]],
];

// Best-effort synchronous resolution of a destination name to [lat, lng].
// Returns null when the name doesn't match any known place.
export function resolveCoordinates(name) {
  if (!name) return null;
  const lower = String(name).toLowerCase();
  const hit = GAZETTEER.find(([key]) => lower.includes(key));
  return hit ? hit[1] : null;
}